import React, { useState } from 'react';
import {
    Autocomplete,
    Container,
    Divider,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { textFieldStyles } from '../styles.js';
import { PaginationController } from './PaginationController.jsx';
import { LoadingIndicator } from './generic.jsx';
// In order to dynamically pass in different search bars to query on, we use 'autoCompleteOptions' prop object.
// Since the auto complete depends on state that exists inside PaginatedListTable (setSearchVal), I cannot pass in an autocomplete component,
// have to instead pass in the options object.
export function PaginatedListTable({
    requestHook,
    TableComponent,
    sortChoices = [],
    sortMap = {},
    defaultSort = {},
    autoCompleteOptions = {
        searchParam: '',
        optionsLabel: '',
        useOptions: () => {
            return { data: [], isPending: false };
        },
        reduceOptions: () => [],
        useOptionLabel: true,
    },
    extraParams = {},
}) {
    const { searchParam, optionsLabel, useOptions, reduceOptions, useOptionLabel } =
        autoCompleteOptions;

    const _getOrdering = str => {
        return sortMap?.[str] || str;
    };

    const [page, setPage] = useState(1);
    const [orderDir, setOrderDir] = useState(defaultSort?.orderDir || 'asc');
    const [ordering, setOrdering] = useState(defaultSort?.ordering || 'name');
    const [searchVal, setSearchVal] = useState('');
    let qParam = searchParam ? { [searchParam]: searchVal } : {};
    qParam = { ...qParam, ...extraParams };
    const { data, isPending, error } = requestHook({
        page,
        ordering: _getOrdering(ordering),
        orderDir,
        ...qParam,
    });
    const { data: optionsData, isPending: isOptionsPending } = useOptions();

    const handleOrderDirChange = e => {
        return setOrderDir(e.target.value);
    };

    const handleOrderingChange = e => {
        return setOrdering(e.target.value);
    };

    if (error) return <>{error.message}</>;

    const selectStyles = {
        minWidth: 140,
        '& .MuiOutlinedInput-root': {
            color: 'white',
            '& fieldset': {
                borderColor: 'rgba(255,255,255,0.23)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255,255,255,0.5)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#66b2ff',
            },
        },
        '& .MuiSelect-icon': {
            color: 'rgba(255,255,255,0.7)',
        },
    };

    const labelStyles = {
        color: 'rgba(255,255,255,0.7)',
        '&.Mui-focused': {
            color: '#66b2ff',
        },
    };

    return (
        <Container
            sx={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: '20px',
            }}
        >
            <Stack
                direction="row"
                spacing={3}
                useFlexGap
                sx={{
                    width: '100%',
                    marginTop: 4,
                    marginBottom: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                {searchParam && (
                    <Autocomplete
                        sx={{ width: 350 }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={optionsLabel}
                                placeholder={`Search ${optionsLabel}...`}
                                sx={textFieldStyles}
                                size="small"
                                slotProps={{
                                    ...params.slotProps,
                                    input: {
                                        ...params.slotProps.input,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <SearchIcon
                                                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                                                    />
                                                </InputAdornment>
                                                {params.slotProps.input.startAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )}
                        options={
                            !isOptionsPending
                                ? reduceOptions(optionsData)
                                : []
                        }
                        getOptionLabel={option => option.label || ''}
                        getOptionKey={option => option.id}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, option) => {
                            setSearchVal(useOptionLabel ? option.label : option.id);
                        }}
                    />
                )}
                {searchParam && (
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ borderColor: 'rgba(255,255,255,0.12)' }}
                    />
                )}
                { sortChoices.length > 0 && (
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl size="small" sx={selectStyles}>
                        <InputLabel sx={labelStyles}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <SortIcon fontSize="small" />
                                <span>Sort By</span>
                            </Stack>
                        </InputLabel>
                        <Select
                            label="Sort By..."
                            onChange={handleOrderingChange}
                            value={ordering}
                            MenuProps={{ disableScrollLock: true }}
                        >
                            {sortChoices.map((choice, i) => (
                                <MenuItem key={i} value={choice}>
                                    {choice}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={selectStyles}>
                        <InputLabel sx={labelStyles}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <SwapVertIcon fontSize="small" />
                                <span>Order</span>
                            </Stack>
                        </InputLabel>
                        <Select
                            label="Order..."
                            onChange={handleOrderDirChange}
                            value={orderDir}
                            MenuProps={{ disableScrollLock: true }}
                        >
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
                )}
            </Stack>
            {isPending ? (
                <LoadingIndicator />
            ) : (
                <>
                    <Stack
                        direction="row"
                        sx={{
                            width: '100%',
                            marginTop: 3,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {data.count} results
                        </Typography>
                        <PaginationController
                            page={page}
                            setPage={setPage}
                            previous={data.previous}
                            next={data.next}
                            styles={{ marginBottom: 0 }}
                        />
                    </Stack>
                    <Box
                        sx={{
                            background: '#1a1a1a',
                            border: '1px solid #2a2a2a',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            marginTop: '20px',
                        }}
                    >
                        <TableComponent
                            sortable={false}
                            data={data.results}
                        />
                    </Box>
                </>
            )}
        </Container>
    );
}
